import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { serverStore } from '../../stores/server.store';
import { Button } from '../design/Button';
import { Card, CardHeader, CardTitle, CardContent } from '../design/Card';
import { apiFetch } from '../../utils/api';
import toast from 'react-hot-toast';
import { Download, Box, Layers, Sparkles, Zap, AlertTriangle, Cpu, ChevronDown, Package } from 'lucide-react';
import { cn } from '../../utils/cn';
import { ModBrowserModal } from '../modals/ModBrowserModal';
import { InstalledContentCard } from '../server/InstalledContentCard';
import { Select } from '../design/Select';
import { FunLoader } from '../common/FunLoader';

type ServerFlavor = 'vanilla' | 'fabric' | 'paper' | 'purpur';

// Loader-Familien (der ECHTE Loader kommt vom Backend /loader, nicht aus dem Typ).
const MOD_LOADERS = ['fabric', 'quilt', 'forge', 'neoforge'];
const PLUGIN_LOADERS = ['paper', 'purpur', 'spigot', 'bukkit', 'folia'];

const FLAVOR_BUTTONS: { id: ServerFlavor; label: string; icon: React.ReactNode }[] = [
    { id: 'vanilla', label: 'Vanilla', icon: <Box size={20} className="mb-1.5" /> },
    { id: 'fabric', label: 'Fabric', icon: <Layers size={20} className="mb-1.5" /> },
    { id: 'paper', label: 'Paper', icon: <Sparkles size={20} className="mb-1.5" /> },
    { id: 'purpur', label: 'Purpur', icon: <Zap size={20} className="mb-1.5" /> },
];

const toFlavor = (type?: string): ServerFlavor =>
    type === 'fabric' || type === 'paper' || type === 'purpur' ? type : 'vanilla';

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const VanillaTab = observer(() => {
    const activeServer = serverStore.activeServer;
    const [versions, setVersions] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [installing, setInstalling] = useState(false);

    const [serverType, setServerType] = useState<ServerFlavor>(toFlavor(activeServer?.type));
    const [selectedMcVersion, setSelectedMcVersion] = useState(activeServer?.version || '1.21.1');

    // Der TATSAECHLICHE Loader (fabric/quilt/forge/neoforge/vanilla/paper...)
    // vom Backend erkannt -> entscheidet Mods vs Plugins vs Datapacks-only.
    const [detectedLoader, setDetectedLoader] = useState<string>('vanilla');

    const [showModBrowser, setShowModBrowser] = useState(false);
    const [browserType, setBrowserType] = useState<'mod' | 'datapack' | 'plugin'>('mod');
    const [refreshSignal, setRefreshSignal] = useState(0);
    const [showSoftware, setShowSoftware] = useState(false);

    const isModded = MOD_LOADERS.includes(detectedLoader);
    const isPluginServer = PLUGIN_LOADERS.includes(detectedLoader);

    useEffect(() => {
        if (activeServer) {
            setServerType(toFlavor(activeServer.type));
            if (activeServer.version) setSelectedMcVersion(activeServer.version);
        }
    }, [activeServer]);

    // Echten Loader beim Serverwechsel vom Backend holen. Vanilla-Server:
    // Software-Wechsel ist DIE Kernfunktion -> aufgeklappt; sonst eingeklappt
    // (gefaehrlich fuer Modpacks).
    useEffect(() => {
        if (!activeServer) return;
        (async () => {
            try {
                const res = await apiFetch(`/api/servers/${activeServer.id}/loader`);
                if (res.ok) {
                    const d = await res.json();
                    if (d.loader) {
                        setDetectedLoader(d.loader);
                        setShowSoftware(d.loader === 'vanilla');
                    }
                    if (d.mcVersion) setSelectedMcVersion(d.mcVersion);
                }
            } catch { /* Default vanilla */ }
        })();
    }, [activeServer?.id]);

    useEffect(() => {
        fetchVersions(serverType);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serverType]);

    const fetchVersions = async (flavor: ServerFlavor) => {
        setLoading(true);
        try {
            if (flavor === 'paper' || flavor === 'purpur') {
                const res = await apiFetch(`/api/proxies/${flavor}/versions`);
                if (res.ok) {
                    const data: string[] = await res.json();
                    setVersions(data.map((v) => ({ id: v, type: 'release' })));
                }
            } else {
                const res = await apiFetch('/api/proxies/vanilla/versions');
                if (res.ok) setVersions(await res.json());
            }
        } catch (e) {
            console.error(e);
            toast.error('Versionen konnten nicht geladen werden');
        } finally {
            setLoading(false);
        }
    };

    const handleInstall = async () => {
        if (!activeServer || !selectedMcVersion) return;
        const flavorLabel = FLAVOR_BUTTONS.find((f) => f.id === serverType)?.label || serverType;
        if (!confirm(`${flavorLabel} für MC ${selectedMcVersion} installieren?\nDies überschreibt die Server-Jar! (Bei Modpacks NICHT nutzen.)`)) return;
        setInstalling(true);
        try {
            let res: Response;
            if (serverType === 'fabric') {
                res = await apiFetch(`/api/servers/${activeServer.id}/fabric`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ mcVersion: selectedMcVersion, loaderVersion: '0.16.10' }),
                });
            } else if (serverType === 'paper' || serverType === 'purpur') {
                res = await apiFetch(`/api/servers/${activeServer.id}/paper/install`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ mcVersion: selectedMcVersion, flavor: serverType, restart: true }),
                });
            } else {
                const versionObj = versions.find((v) => v.id === selectedMcVersion);
                if (!versionObj) throw new Error('Version URL nicht gefunden');
                res = await apiFetch(`/api/servers/${activeServer.id}/vanilla/install`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ versionUrl: versionObj.url, createBackup: false, restart: true }),
                });
            }
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.message || 'Installation fehlgeschlagen');
            }
            toast.success('Installation erfolgreich! Server neustarten.');
        } catch (e: any) {
            console.error(e);
            toast.error(e.message || 'Installation fehlgeschlagen');
        } finally {
            setInstalling(false);
        }
    };

    const openBrowser = (t: 'mod' | 'datapack' | 'plugin') => {
        setBrowserType(t);
        setShowModBrowser(true);
    };

    if (!activeServer) return null;

    return (
        <div className="space-y-5 animate-in fade-in duration-300">
            <ModBrowserModal
                isOpen={showModBrowser}
                onClose={() => { setShowModBrowser(false); setRefreshSignal((n) => n + 1); }}
                serverId={activeServer.id}
                mcVersion={selectedMcVersion}
                loader={detectedLoader}
                type={browserType}
            />

            {/* Loader-Hero */}
            <div className="rounded-2xl border border-ember/25 bg-gradient-to-r from-ember/10 via-surface-2 to-surface-2 p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-ember/15 border border-ember/25 flex items-center justify-center shrink-0">
                    <Cpu className="text-ember" size={26} />
                </div>
                <div className="mr-auto min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="text-2xl font-bold text-white capitalize">{detectedLoader}</h2>
                        <span className="text-xs bg-surface-2 border border-line rounded-md px-2 py-0.5 text-fg-muted">MC {selectedMcVersion}</span>
                    </div>
                    <p className="text-sm text-fg-muted mt-1">
                        {isModded ? 'Modded-Server — verwalte deine Mods unten oder füge neue hinzu.'
                            : isPluginServer ? 'Bukkit-API-Server — verwalte deine Plugins unten.'
                            : 'Vanilla-Server — Datapacks möglich; für Mods brauchst du einen Loader (unten wechselbar).'}
                    </p>
                </div>
                {(isModded || isPluginServer) && (
                    <Button className="bg-ember hover:bg-ember/80 shrink-0" onClick={() => openBrowser(isModded ? 'mod' : 'plugin')}>
                        <Package size={16} className="mr-2" /> {isModded ? 'Mods' : 'Plugins'} hinzufügen
                    </Button>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* Hauptspalte: Mods/Plugins */}
                <div className="lg:col-span-2 space-y-5">
                    {isModded && (
                        <InstalledContentCard
                            serverId={activeServer.id}
                            folder="mods"
                            title={`Installierte Mods (${cap(detectedLoader)})`}
                            onBrowse={() => openBrowser('mod')}
                            refreshSignal={refreshSignal}
                            accentIconClass="text-ember"
                            accentButtonClass="border-ember/50 text-ember hover:bg-ember/10"
                            accentBadgeClass="bg-ember/15 text-ember"
                        />
                    )}
                    {isPluginServer && (
                        <InstalledContentCard
                            serverId={activeServer.id}
                            folder="plugins"
                            title="Installierte Plugins"
                            onBrowse={() => openBrowser('plugin')}
                            refreshSignal={refreshSignal}
                            accentIconClass="text-ember"
                            accentButtonClass="border-ember/50 text-ember hover:bg-ember/10"
                            accentBadgeClass="bg-ember/15 text-ember"
                        />
                    )}
                    {!isModded && !isPluginServer && (
                        <Card className="bg-ink border-line-strong text-white border-dashed" noPadding>
                            <CardContent className="py-12 text-center text-fg-muted">
                                <Package size={40} className="mx-auto mb-3 opacity-25" />
                                <p className="text-sm">Vanilla-Server haben keine Mods/Plugins.</p>
                                <p className="text-xs mt-1">Installiere rechts Fabric (Mods) oder Paper/Purpur (Plugins).</p>
                            </CardContent>
                        </Card>
                    )}
                </div>

                {/* Seitenspalte: Datapacks + Software-Wechsel */}
                <div className="space-y-5">
                    <Card className="bg-ink border-line-strong text-white" noPadding>
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="text-base flex items-center"><Box className="mr-2 text-ember" size={18} /> Datapacks</CardTitle>
                            <Button size="sm" variant="outline" className="border-ember/50 text-ember hover:bg-ember/10" onClick={() => openBrowser('datapack')}>
                                Hinzufügen
                            </Button>
                        </CardHeader>
                        <CardContent>
                            <p className="text-xs text-fg-muted">Landen automatisch in <code>world/datapacks</code> — funktionieren auf jedem Server-Typ.</p>
                        </CardContent>
                    </Card>

                    {/* Server-Software wechseln: fuer Modded/Plugin-Server eingeklappte
                        Gefahrenzone (ueberschreibt die Jar!), fuer Vanilla offen. */}
                    <Card className={cn('bg-ink text-white', showSoftware ? 'border-red-500/25' : 'border-line-strong')} noPadding>
                        <button className="w-full" onClick={() => setShowSoftware((v) => !v)}>
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle className="text-base flex items-center text-fg-muted">
                                    <Download className="mr-2" size={18} /> Server-Software wechseln
                                </CardTitle>
                                <ChevronDown size={18} className={cn('text-fg-muted transition-transform', showSoftware && 'rotate-180')} />
                            </CardHeader>
                        </button>
                        {showSoftware && (
                            <CardContent className="space-y-4">
                                {(isModded || isPluginServer) && (
                                    <div className="flex items-start gap-2 text-[11px] text-red-300/90 bg-red-500/10 border border-red-500/20 rounded-lg px-2.5 py-2">
                                        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                                        <span><strong>Achtung:</strong> überschreibt die Server-Jar. Bei einem Modpack/Loader-Server zerstört das die Installation!</span>
                                    </div>
                                )}
                                <div className="grid grid-cols-2 gap-2">
                                    {FLAVOR_BUTTONS.map((f) => (
                                        <button key={f.id} onClick={() => setServerType(f.id)}
                                            className={cn('p-3 rounded-xl border flex flex-col items-center justify-center transition-all text-sm',
                                                serverType === f.id ? 'bg-ember/10 border-ember/50 text-ember' : 'bg-surface-2 border-line hover:bg-white/5 text-fg-muted')}>
                                            {f.icon}<span className="font-bold">{f.label}</span>
                                        </button>
                                    ))}
                                </div>
                                <Select label="Version" value={selectedMcVersion} onChange={setSelectedMcVersion}
                                    placeholder={loading ? 'Lade Versionen…' : selectedMcVersion}
                                    options={versions.length > 0
                                        ? versions.map((v: any) => ({ value: v.id, label: v.id, hint: v.type === 'release' ? undefined : 'Snapshot' }))
                                        : [{ value: selectedMcVersion, label: selectedMcVersion }]} />
                                <Button className="w-full bg-blue-600 hover:bg-blue-500" onClick={handleInstall} disabled={installing}>
                                    {installing ? <FunLoader size="sm" showProgress={false} /> : (<><Download className="mr-2" size={16} />{FLAVOR_BUTTONS.find((f) => f.id === serverType)?.label} installieren</>)}
                                </Button>
                            </CardContent>
                        )}
                    </Card>
                </div>
            </div>
        </div>
    );
});

export default VanillaTab;
